import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { toastr } from "react-redux-toastr";
import { hideModal } from "src/actions/modal.actions";
import { getModalData, getProducts } from "src/selectors";
import { getPlans, getPlan } from "src/actions/products.action";
import { createPlanFeatureDiscount, getPlanFeatureDiscounts, deletePlanFeatureDiscount } from "src/actions/accountDetailsActions/billing.actions";
import { groupStyles } from "src/utils/SelectStyles";

const ALL_OPTION = { value: '__ALL__', label: 'ALL' };

const makeBlankCard = (tempId) => ({
  tempId,
  selectedPlan: null,
  selectedFeature: null,
  featureOptions: [],
  isLoadingFeatures: false,
});

class ApplyDiscountModal extends Component {
  state = {
    isLoading: true,
    assignments: [],
    toBeRemoved: {},   // { [assignmentId]: true } — queued for deletion on Save
    cardStates: {},    // editable dropdown state per existing assignment
    newCards: [],      // pending additions
    nextTempId: 1,
    isSaving: false,
  };

  initCardStates(assignments) {
    const cardStates = {};
    assignments.forEach((a) => {
      cardStates[a.id] = {
        selectedPlan: a.planId ? { value: a.planId, label: a.planName } : null,
        selectedFeature: a.featureId ? { value: a.featureId, label: a.featureName } : ALL_OPTION,
        featureOptions: a.featureId ? [ALL_OPTION, { value: a.featureId, label: a.featureName }] : [ALL_OPTION],
        isLoadingFeatures: false,
      };
    });
    return cardStates;
  }

  componentDidMount() {
    const discount = this.props.modalProps.value;
    Promise.all([
      this.props.getPlans(),
      this.props.getPlanFeatureDiscounts(),
    ]).then(([, assignmentsResponse]) => {
      const all = assignmentsResponse?.data || [];
      const match = all.find((entry) => entry.discount?.id === discount?.id);
      const assignments = match?.planFeatureDiscounts || [];
      this.setState({
        isLoading: false,
        assignments,
        cardStates: this.initCardStates(assignments),
        newCards: assignments.length === 0 ? [makeBlankCard(1)] : [],
        nextTempId: 2,
      });
    });
  }

  buildFeatureOptions(planData) {
    const features = (planData?.features || []).map((f) => ({ value: f.id, label: f.name }));
    const packageFeatures = (planData?.featurePackages || []).flatMap((p) =>
      (p.featurePackageOptions || []).flatMap((opt) =>
        (opt.features || []).map((f) => ({ value: f.id, label: f.name }))
      )
    );
    const seenIds = new Set();
    const dedupedFeatures = [...features, ...packageFeatures]
      .filter((f) => {
        if (seenIds.has(f.value)) return false;
        seenIds.add(f.value);
        return true;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    return [ALL_OPTION, ...dedupedFeatures];
  }

  // --- Cross-row exclusion helpers (based on current state) ---

  computeActiveRows() {
    const { assignments, toBeRemoved, cardStates, newCards } = this.state;
    return [
      ...assignments.filter((a) => !toBeRemoved[a.id]).map((a) => ({
        rowKey: `a-${a.id}`,
        planId: cardStates[a.id]?.selectedPlan?.value,
        featureValue: cardStates[a.id]?.selectedFeature?.value,
      })),
      ...newCards.map((c) => ({
        rowKey: `n-${c.tempId}`,
        planId: c.selectedPlan?.value,
        featureValue: c.selectedFeature?.value,
      })),
    ].filter((r) => r.planId && r.featureValue);
  }

  getExcludedFeatureIds(rowKey, planId) {
    return new Set(
      this.computeActiveRows()
        .filter((r) => r.rowKey !== rowKey && r.planId === planId)
        .map((r) => r.featureValue)
    );
  }

  getExcludedPlanIds(rowKey) {
    return new Set(
      this.computeActiveRows()
        .filter((r) => r.rowKey !== rowKey && r.featureValue === ALL_OPTION.value)
        .map((r) => r.planId)
    );
  }

  // --- Existing assignment card handlers ---

  onCardPlanChange = (assignmentId, option) => {
    this.setState((prev) => ({
      cardStates: {
        ...prev.cardStates,
        [assignmentId]: {
          ...prev.cardStates[assignmentId],
          selectedPlan: option,
          selectedFeature: null,
          featureOptions: [],
          isLoadingFeatures: !!option,
        },
      },
    }));

    if (!option) return;

    this.props.getPlan(option.value).then((planData) => {
      const featureOptions = this.buildFeatureOptions(planData);
      if (featureOptions.length <= 1) {
        toastr.error('This plan has no features and cannot be selected.', { timeOut: 3000, position: 'top-center' });
        this.setState((prev) => ({
          cardStates: {
            ...prev.cardStates,
            [assignmentId]: {
              ...prev.cardStates[assignmentId],
              selectedPlan: null,
              featureOptions: [],
              isLoadingFeatures: false,
            },
          },
        }));
        return;
      }
      const excluded = this.getExcludedFeatureIds(`a-${assignmentId}`, option.value);
      const defaultFeature = excluded.has(ALL_OPTION.value) ? null : ALL_OPTION;
      this.setState((prev) => ({
        cardStates: {
          ...prev.cardStates,
          [assignmentId]: {
            ...prev.cardStates[assignmentId],
            selectedFeature: defaultFeature,
            featureOptions,
            isLoadingFeatures: false,
          },
        },
      }));
    });
  };

  onCardFeatureChange = (assignmentId, option) => {
    this.setState((prev) => ({
      cardStates: {
        ...prev.cardStates,
        [assignmentId]: { ...prev.cardStates[assignmentId], selectedFeature: option },
      },
    }));
  };

  onMarkForRemoval = (id) => {
    this.setState((prev) => ({ toBeRemoved: { ...prev.toBeRemoved, [id]: true } }));
  };

  // --- New blank card handlers ---

  updateNewCard = (tempId, updates) => {
    this.setState((prev) => ({
      newCards: prev.newCards.map((c) => c.tempId === tempId ? { ...c, ...updates } : c),
    }));
  };

  onNewCardPlanChange = (tempId, option) => {
    this.updateNewCard(tempId, {
      selectedPlan: option,
      selectedFeature: null,
      featureOptions: [],
      isLoadingFeatures: !!option,
    });

    if (!option) return;

    this.props.getPlan(option.value).then((planData) => {
      const featureOptions = this.buildFeatureOptions(planData);
      if (featureOptions.length <= 1) {
        toastr.error('This plan has no features and cannot be selected.', { timeOut: 3000, position: 'top-center' });
        this.updateNewCard(tempId, {
          selectedPlan: null,
          featureOptions: [],
          isLoadingFeatures: false,
        });
        return;
      }
      const excluded = this.getExcludedFeatureIds(`n-${tempId}`, option.value);
      const defaultFeature = excluded.has(ALL_OPTION.value) ? null : ALL_OPTION;
      this.updateNewCard(tempId, {
        selectedFeature: defaultFeature,
        featureOptions,
        isLoadingFeatures: false,
      });
    });
  };

  onNewCardFeatureChange = (tempId, option) => {
    this.updateNewCard(tempId, { selectedFeature: option });
  };

  onDiscardNewCard = (tempId) => {
    this.setState((prev) => ({
      newCards: prev.newCards.filter((c) => c.tempId !== tempId),
    }));
  };

  addNewCard = () => {
    this.setState((prev) => ({
      newCards: [...prev.newCards, makeBlankCard(prev.nextTempId)],
      nextTempId: prev.nextTempId + 1,
    }));
  };

  // --- Bottom actions ---

  findAllOptionConflict = () => {
    const { toBeRemoved, cardStates, newCards, assignments } = this.state;
    const rows = [
      ...assignments.filter((a) => !toBeRemoved[a.id]).map((a) => cardStates[a.id]).filter(Boolean),
      ...newCards,
    ].filter((r) => r.selectedPlan && r.selectedFeature);

    const byPlan = {};
    rows.forEach((r) => {
      const planId = r.selectedPlan.value;
      if (!byPlan[planId]) byPlan[planId] = { planName: r.selectedPlan.label, features: [] };
      byPlan[planId].features.push(r.selectedFeature);
    });

    for (const planId of Object.keys(byPlan)) {
      const { planName, features } = byPlan[planId];
      const hasAll = features.some((f) => f.value === ALL_OPTION.value);
      const individualFeatures = features.filter((f) => f.value !== ALL_OPTION.value);
      if (hasAll && individualFeatures.length > 0) {
        const featureNames = individualFeatures.map((f) => `"${f.label}"`).join(', ');
        return `Plan "${planName}" has both the "ALL" option and the individual feature ${featureNames} selected. Please remove either the individual feature or the "ALL" option.`;
      }
    }
    return null;
  };

  onSave = () => {
    const { toBeRemoved, newCards, assignments, cardStates } = this.state;
    const discount = this.props.modalProps.value;

    const visibleAssignmentIds = assignments.filter((a) => !toBeRemoved[a.id]).map((a) => a.id);
    const hasIncompleteExisting = visibleAssignmentIds.some((id) => {
      const cs = cardStates[id] || {};
      return cs.selectedPlan && !cs.selectedFeature;
    });
    const hasIncompleteNew = newCards.some((c) => c.selectedPlan && !c.selectedFeature);

    if (hasIncompleteExisting || hasIncompleteNew) {
      toastr.warning(
        'Each row must have both a plan and a feature selected. Please complete or remove any incomplete rows before saving.',
        { timeOut: 4000, position: 'top-center' }
      );
      return;
    }

    const allOptionConflict = this.findAllOptionConflict();
    if (allOptionConflict) {
      toastr.error(allOptionConflict, { timeOut: 5000, position: 'top-center' });
      return;
    }

    const removeIds = Object.keys(toBeRemoved).map(Number);
    const cardsToAdd = newCards.filter((c) => c.selectedPlan && c.selectedFeature);

    if (removeIds.length === 0 && cardsToAdd.length === 0) {
      this.props.hideModal();
      return;
    }

    this.setState({ isSaving: true });

    const removePromises = removeIds.map((id) => this.props.deletePlanFeatureDiscount(id));
    const addPromises = cardsToAdd.map((c) => {
      const featureId = c.selectedFeature.value === '__ALL__' ? null : c.selectedFeature.value;
      return this.props.createPlanFeatureDiscount(discount.id, c.selectedPlan.value, featureId);
    });

    Promise.all([...removePromises, ...addPromises])
      .then((results) => {
        const anyFailed = results.some((r) => !r?.data?.success);
        if (anyFailed) {
          toastr.error('Some changes could not be saved.', { timeOut: 3000, position: 'top-center' });
          this.setState({ isSaving: false });
        } else {
          toastr.success('Changes saved successfully!', { timeOut: 2000, position: 'top-center' });
          this.setState({ isSaving: false });
          if (typeof this.props.modalProps.onSuccess === 'function') this.props.modalProps.onSuccess();
          this.props.hideModal();
        }
      })
      .catch((err) => {
        console.warn('ApplyDiscountModal save error:', err);
        toastr.error('An unexpected error occurred.', { timeOut: 3000, position: 'top-center' });
        this.setState({ isSaving: false });
      });
  };

  render() {
    const { isLoading, assignments, toBeRemoved, cardStates, newCards, isSaving } = this.state;
    const { modalProps, plans } = this.props;
    const discount = modalProps.value;

    const planOptions = (plans || [])
      .filter((p) => p.active)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => ({ value: p.planId, label: p.name }));

    const visibleAssignments = assignments
      .filter((a) => !toBeRemoved[a.id])
      .sort((a, b) => {
        const planCmp = (a.planName || '').localeCompare(b.planName || '');
        if (planCmp !== 0) return planCmp;
        return (a.featureName || '').localeCompare(b.featureName || '');
      });

    const hasRemoves = Object.keys(toBeRemoved).length > 0;
    const hasCompleteNew = newCards.some((c) => c.selectedPlan && c.selectedFeature);
    const canSave = hasRemoves || hasCompleteNew;

    return (
      <Fragment>
        <div className="modal-backdrop show" />
        <div className="modal" style={{ display: "block" }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Apply Discount: <strong>{discount?.name}</strong>
                </h5>
                <button type="button" className="close" onClick={this.props.hideModal}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="modal-body">
                {/* Existing assignment cards */}
                {visibleAssignments.map((a) => {
                  const cs = cardStates[a.id] || {};
                  return (
                    <div
                      key={a.id}
                      className="border rounded p-3 mb-3 position-relative"
                      style={{ paddingRight: '6.5rem' }}
                    >
                      <button
                        className="btn btn-sm btn-danger position-absolute"
                        style={{ top: '0.75rem', right: '0.75rem', zIndex: 2 }}
                        type="button"
                        disabled={isSaving}
                        onClick={() => this.onMarkForRemoval(a.id)}
                      >
                        REMOVE
                      </button>
                      <div className="form-row">
                        <div className="col-md-6">
                          <label>Plan</label>
                          <Select
                            styles={groupStyles}
                            options={planOptions.filter((opt) => !this.getExcludedPlanIds(`a-${a.id}`).has(opt.value))}
                            value={cs.selectedPlan || null}
                            onChange={(opt) => this.onCardPlanChange(a.id, opt)}
                            isLoading={isLoading}
                            isClearable={true}
                          />
                        </div>
                        <div className="col-md-6">
                          <label>Feature</label>
                          <Select
                            styles={groupStyles}
                            placeholder={cs.selectedPlan ? "Select a feature..." : "Select a plan first..."}
                            options={(cs.featureOptions || []).filter((opt) => !this.getExcludedFeatureIds(`a-${a.id}`, cs.selectedPlan?.value).has(opt.value))}
                            value={cs.selectedFeature || null}
                            onChange={(opt) => this.onCardFeatureChange(a.id, opt)}
                            isLoading={cs.isLoadingFeatures}
                            isDisabled={!cs.selectedPlan || cs.isLoadingFeatures}
                            isClearable={true}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* New blank cards */}
                {newCards.map((card) => (
                  <div
                    key={card.tempId}
                    className="border rounded p-3 mb-3 position-relative"
                    style={{ paddingRight: '6.5rem' }}
                  >
                    <button
                      className="btn btn-sm btn-danger position-absolute"
                      style={{ top: '0.75rem', right: '0.75rem', zIndex: 2 }}
                      type="button"
                      disabled={isSaving}
                      onClick={() => this.onDiscardNewCard(card.tempId)}
                    >
                      REMOVE
                    </button>
                    <div className="form-row">
                      <div className="col-md-6">
                        <label>Plan</label>
                        <Select
                          styles={groupStyles}
                          placeholder="Select a plan..."
                          options={planOptions.filter((opt) => !this.getExcludedPlanIds(`n-${card.tempId}`).has(opt.value))}
                          value={card.selectedPlan}
                          onChange={(opt) => this.onNewCardPlanChange(card.tempId, opt)}
                          isLoading={isLoading}
                          isDisabled={isLoading}
                          isClearable={true}
                        />
                      </div>
                      <div className="col-md-6">
                        <label>Feature</label>
                        <Select
                          styles={groupStyles}
                          placeholder={card.selectedPlan ? "Select a feature..." : "Select a plan first..."}
                          options={(card.featureOptions || []).filter((opt) => !this.getExcludedFeatureIds(`n-${card.tempId}`, card.selectedPlan?.value).has(opt.value))}
                          value={card.selectedFeature}
                          onChange={(opt) => this.onNewCardFeatureChange(card.tempId, opt)}
                          isLoading={card.isLoadingFeatures}
                          isDisabled={!card.selectedPlan || card.isLoadingFeatures}
                          isClearable={true}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {/* Bottom action bar */}
                <div className="d-flex justify-content-end mt-3" style={{ gap: '0.5rem' }}>
                  <button
                    className="btn btn-outline-primary"
                    type="button"
                    disabled={isSaving}
                    onClick={this.addNewCard}
                  >
                    ADD ANOTHER
                  </button>
                  <button
                    className="btn btn-secondary"
                    type="button"
                    disabled={isSaving}
                    onClick={this.props.hideModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    type="button"
                    disabled={isSaving || !canSave}
                    onClick={this.onSave}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  modalProps: getModalData(state).modalProps,
  plans: getProducts(state).plans,
});

const mapDispatchToProps = {
  hideModal,
  getPlans,
  getPlan,
  createPlanFeatureDiscount,
  getPlanFeatureDiscounts,
  deletePlanFeatureDiscount,
};

export default connect(mapStateToProps, mapDispatchToProps)(ApplyDiscountModal);